'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardHeader } from '@ahafood/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Image } from '@ahafood/ui/components/image';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@ahafood/ui/components/sheet';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTrigger,
} from '@ahafood/ui/components/stepper';
import { Tabs, TabsContent } from '@ahafood/ui/components/tabs';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  ChevronLeft,
  Clock,
  House,
  MapPin,
  Package,
  Phone,
  Star,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { LoginDialog } from '../(main)/store/components/fb-login';
import { useCancelOrder } from '@/api/use-cancel-order';
import { useGetOrderDetail } from '@/api/use-get-order-detail';
import { type Order, useGetOrderHistory } from '@/api/use-get-order-history';
import { useOrderNotificationSSE } from '@/api/use-order-notification';
import { useNavigation } from '@/components/navigation/navigation-context';
import { ROUTES } from '@/config/router';
// import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { usePathname, useRouter } from '@/i18n/routing';
import { getAuthToken } from '@/lib/auth/get-token';
import { DateFormat, dayjs, formatUnixTime } from '@/lib/datetime';
import { trackEvent } from '@/lib/event-tracking';
// import { useIsMobile } from '@/lib/is-mobile';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useRating } from '@/providers/rating-provider';
import { useAuthStore } from '@/store';

interface OrderNotificationsProps {
  orderId?: string | null;
  onClose?: () => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function OrderNotifications({
  orderId,
  onClose,
}: OrderNotificationsProps = {}) {
  const [activeTab, setActiveTab] = useState('listing');
  const token = getAuthToken();
  const router = useRouter();
  const isWebinApp = useDetectWebInApp();
  const { user } = useAuthStore();
  const { openRatingModal, openCommentModal } = useRating();
  // const routes = useCityRoutes();
  // const isMobile = useIsMobile();
  const pathName = usePathname();
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showTrackingSheet, setShowTrackingSheet] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState<string>('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showWaitingDialog, setShowWaitingDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelComment, setCancelComment] = useState<string>('');
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const [cancelButtonEnabled, setCancelButtonEnabled] = useState(false);
  const { isNavigationVisible, setForceHidden } = useNavigation();
  const routes = useCityRoutes();
  const isMobile = useIsMobile();

  // Hide navigation when tracking sheet is open
  useEffect(() => {
    if (showTrackingSheet) {
      console.log(
        '[OrderNotifications] Tracking sheet open, hiding navigation'
      );
      setForceHidden(true);
      document.documentElement.dataset.trackingSheetOpen = 'true';
      return () => {
        console.log(
          '[OrderNotifications] Tracking sheet closed, restoring navigation'
        );
        setForceHidden(false);
        delete document.documentElement.dataset.trackingSheetOpen;
      };
    }
  }, [showTrackingSheet, setForceHidden]);

  // Auto-close tracking sheet when tab becomes hidden to prevent overlay stacking
  useEffect(() => {
    if (!showTrackingSheet) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[OrderNotifications] Tab hidden, closing tracking sheet');
        setShowTrackingSheet(false);
        setTrackingUrl('');
      }
    };

    const handlePageHide = () => {
      console.log('[OrderNotifications] Page hiding, closing tracking sheet');
      setShowTrackingSheet(false);
      setTrackingUrl('');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [showTrackingSheet]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  // Handle initial orderId - similar to StoreItemSheet logic
  useEffect(() => {
    if (orderId) {
      setSelectedOrder(orderId);
      setActiveTab('detail');
    }
  }, [orderId]);

  const { data: orderHistory, isLoading: historyLoading } = useGetOrderHistory(
    token,
    0,
    40,
    {
      refetchInterval: 5000,
      enabled: activeTab === 'listing',
    }
  );

  // Get order detail (only when order is selected)
  const { data: orderDetail, isLoading: detailLoading } = useGetOrderDetail(
    token,
    selectedOrder!,
    {
      enabled: !!token && !!selectedOrder && activeTab === 'detail',
      refetchInterval: 5000,
    }
  );

  // Auto-refresh cancel button state every minute
  useEffect(() => {
    if (!orderDetail || activeTab !== 'detail') return;

    const updateCancelButtonState = () => {
      setCancelButtonEnabled(isCancelButtonEnabled(orderDetail));
    };

    // Update immediately
    updateCancelButtonState();

    // Set up interval to check every minute
    const interval = setInterval(updateCancelButtonState, 60000);

    return () => clearInterval(interval);
  }, [orderDetail, activeTab]);

  // Cancel order mutation
  const cancelOrderMutation = useCancelOrder(token, {
    onSuccess: () => {
      // Refetch order history and detail after successful cancellation
      void queryClient.invalidateQueries({ queryKey: ['order-history'] });
      void queryClient.invalidateQueries({
        queryKey: ['order-detail', token, selectedOrder],
      });
      // Also refetch the specific order history query
      void queryClient.refetchQueries({
        queryKey: ['order-history', token, 0, 40],
      });
      setShowCancelDialog(false);
      setCancelReason('');
      setCancelComment('');
    },
    onError: (error) => {
      console.error('Cancel order error:', error);
      toast.error('Đơn hàng hiện không huỷ được, vui lòng kiểm tra lại.');
      // Handle error (show toast or alert)
    },
  });

  // Real-time order status updates via SSE
  const handleOrderNotification = useCallback(
    (notification: any) => {
      console.log('📱 Order notification received:', notification);

      // Show toast notification for status updates
      const statusMessages: Record<string, string> = {
        CONFIRMED: 'Đơn hàng đã được xác nhận! ✅',
        ACCEPTED: 'Tài xế đã nhận đơn! 🚗',
        BOARDED: 'Đơn hàng đang được giao đến bạn! 📦',
        COMPLETED: 'Đơn hàng đã hoàn thành! 🎉',
        CANCELLED: 'Đơn hàng đã bị hủy 😔',
        FAILED: 'Đơn hàng giao không thành công 😔',
      };

      const statusMessage = statusMessages[notification.order_status];
      if (statusMessage) {
        // You can replace this with your toast library
        console.log(`🔔 ${statusMessage}`);
      }

      // Update order history cache if we have it
      if (orderHistory?.orders) {
        queryClient.setQueryData(
          ['order-history', token, 0, 40],
          (oldData: any) => {
            if (!oldData?.orders) return oldData;

            const updatedOrders = oldData.orders.map((order: Order) => {
              if (order.code === notification.order_code) {
                return {
                  ...order,
                  status: notification.order_status,
                  total_payment:
                    notification.total_payment || order.total_payment,
                };
              }
              return order;
            });

            return {
              ...oldData,
              orders: updatedOrders,
            };
          }
        );
      }

      // Update order detail cache if it matches the current selected order
      if (selectedOrder === notification.order_code && orderDetail) {
        queryClient.setQueryData(
          ['order-detail', token, selectedOrder],
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              status: notification.order_status,
              total_payment:
                notification.total_payment || oldData.total_payment,
            };
          }
        );
      }
    },
    [queryClient, token, orderHistory, selectedOrder, orderDetail]
  );

  // Setup SSE connection for real-time updates
  useOrderNotificationSSE({
    token: isAuthenticated ? token : '',
    onMessage: handleOrderNotification,
  });

  const getStepStatus = (step: number, orderStatus: string) => {
    const statusToStep = {
      CONFIRMING: 1,
      CONFIRMED: 1,
      ASSIGNING: 1, // Looking for driver - step 1 completed

      ACCEPTED: 2, // Driver assigned - step 2 completed
      'IN PROCESS': 3, // Processing - step 2 completed

      BOARDED: 3, // On delivery - step 3 completed

      COMPLETED: 4, // Completed - all steps done

      IN_RETURN: 1,
      RETURNED: 4,
      CANCELLED: 1,
      'BOT CANCELLED': 1,
      FAILED: 1,
      IDLE: 1,
    };

    const currentStep =
      statusToStep[orderStatus as keyof typeof statusToStep] || 1;
    return step <= currentStep;
  };

  const showStepper = orderDetail
    ? !['CANCELLED', 'BOT_CANCELLED', 'FAILED'].includes(orderDetail.status)
    : false;

  const formatTime = (timestamp: number) => {
    return formatUnixTime(timestamp, DateFormat.TIME_24);
  };

  const getStatusInfo = (status: string) => {
    const statusMap = {
      // Trạng thái khởi tạo & xác nhận
      CONFIRMING: {
        text: 'Đang chờ xác nhận',
        description: 'Quán đang xem xét và xác nhận đơn hàng của bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={30}
            height={30}
            className="object-contain"
          />
        ),
        color: 'text-yellow-60',
      },
      CONFIRMED: {
        text: 'Đã xác nhận',
        description: 'Quán đã xác nhận và đang chuẩn bị món ăn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-60',
      },
      // Trạng thái điều phối
      ASSIGNING: {
        text: 'Đang tìm tài xế',
        description: 'Chúng tôi đang tìm tài xế gần nhất để giao đến bạn!',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-50',
      },
      ACCEPTED: {
        text: 'Tài xế đã nhận',
        description: 'Tài xế đang trên đường đến quán để lấy hàng',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step2.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-50',
      },
      // Trạng thái thực hiện
      'IN PROCESS': {
        text: 'Đang giao đến bạn',
        description: 'Hãy chú ý điện thoại, shipper chuẩn bị giao món đến bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-green-60',
      },
      BOARDED: {
        text: 'Đang giao đến bạn',
        description: 'Hãy chú ý điện thoại, shipper chuẩn bị giao món đến bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step3.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-primary-50',
      },
      COMPLETED: {
        text: 'Đã hoàn thành',
        description: 'Cảm ơn và chúc bạn ngon miệng!',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step4.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-green-60',
      },
      // Trạng thái hoàn/trả hàng
      IN_RETURN: {
        text: 'Đang hoàn hàng',
        description: 'Đơn hàng đang được hoàn trả',
        icon: <Package className="size-6" />,
        color: 'text-primary-60',
      },
      RETURNED: {
        text: 'Đã hoàn hàng',
        description: 'Đơn hàng đã được hoàn trả thành công',
        icon: <CheckCircle className="size-6" />,
        color: 'text-neutral-60',
      },
      // Trạng thái hủy/thất bại
      CANCELLED: {
        text: 'Đã hủy',
        description: 'Đơn hàng đã bị hủy',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      BOT_CANCELLED: {
        text: 'Hệ thống hủy',
        description: 'Đơn hàng đã bị hủy bởi hệ thống',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      FAILED: {
        text: 'Thất bại',
        description: 'Đơn hàng giao không thành công',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      // Trạng thái khác
      IDLE: {
        text: 'Đang tìm tài xế',
        description: 'Chúng tôi đang tìm tài xế gần nhất để giao đến bạn!',
        icon: <Clock className="size-6" />,
        color: 'text-neutral-60',
      },
    };
    return statusMap[status as keyof typeof statusMap] || statusMap.CONFIRMING;
  };

  const getStepperValue = (status: string) => {
    const statusToStep = {
      CONFIRMING: 1,
      CONFIRMED: 1,
      ASSIGNING: 1, // Looking for driver - step 1 completed

      ACCEPTED: 2, // Driver assigned - step 2 completed
      'IN PROCESS': 3, // Processing - step 2 completed

      BOARDED: 3, // On delivery - step 3 completed

      COMPLETED: 4, // Completed - all steps done

      IN_RETURN: 1,
      RETURNED: 4,
      CANCELLED: 1,
      'BOT CANCELLED': 1,
      FAILED: 1,
      IDLE: 1,
    };

    return statusToStep[status as keyof typeof statusToStep] || 1;
  };

  const handleOrderSelect = (orderCode: string) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'order_detail',
      button_location: 'order_list',
      user_id: user?.cunstomerId,
    });
    setSelectedOrder(orderCode);
    setActiveTab('detail');
  };

  const handleRatingClick = (e: React.MouseEvent, order: Order) => {
    e.stopPropagation(); // Prevent order detail navigation
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'rating_button',
      button_location: 'order_list',
      user_id: user?.cunstomerId,
      order_code: order.code,
    });
    // Create orderDetail object compatible with openRatingModal
    const orderDetail = {
      code: order.code,
      store_name: order.store_name,
      store_image_url: order.store_image_url
        ? order.store_image_url
        : '/static/images/empty-category.webp',
    };
    openRatingModal(orderDetail);
  };

  const handleCommentClick = (e: React.MouseEvent, order: Order) => {
    e.stopPropagation(); // Prevent order detail navigation
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'comment_button',
      button_location: 'order_list',
      user_id: user?.cunstomerId,
      order_code: order.code,
    });
    // Create orderDetail object and open comment modal with existing rating
    const orderDetail = {
      code: order.code,
      store_name: order.store_name,
      store_image_url: order.store_image_url
        ? order.store_image_url
        : '/static/images/empty-category.webp',
    };
    openCommentModal(orderDetail, order.rating_summary?.overall ?? 5);
  };

  const handleBackToListing = () => {
    if (onClose && orderId) {
      // If we opened with a specific orderId, close the sheet instead of going back to listing
      onClose();
    } else {
      setActiveTab('listing');
      setSelectedOrder(null);
    }
  };

  const handleTrackingClick = (trackingLink: string) => {
    if (isWebinApp) {
      setTrackingUrl(trackingLink);
      setShowTrackingSheet(true);
    } else {
      // Original behavior - open in new tab
      window.open(trackingLink, '_blank');
    }
  };

  // Check if cancel button should be shown
  const shouldShowCancelButton = (order: any) => {
    if (!order) return false;

    // Only show cancel button for orders in these statuses
    const cancelableStatuses = ['CONFIRMING', 'CONFIRMED', 'ASSIGNING', 'IDLE'];
    return cancelableStatuses.includes(order.status);
  };

  // Check if cancel button should be enabled (after 20 minutes)
  const isCancelButtonEnabled = (order: any) => {
    if (!order) return false;

    // Check if 20 minutes have passed since order time
    const now = Date.now();
    const orderTime = order.order_time * 1000; // Convert from Unix timestamp to milliseconds
    const twentyMinutesInMs = 20 * 60 * 1000;

    return now - orderTime >= twentyMinutesInMs;
  };

  const handleCancelClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      order_code: orderDetail?.code,
      button_name: 'cancel_order_button',
      button_location: 'order_list',
      user_id: user?.cunstomerId,
    });
    if (!orderDetail) return;

    // Check if 20 minutes have passed
    if (isCancelButtonEnabled(orderDetail)) {
      // Can cancel - show confirmation dialog
      setCancelReason('user_no_driver_accept');
      setShowCancelDialog(true);
    } else {
      // Cannot cancel yet - show waiting dialog
      setShowWaitingDialog(true);
    }
  };

  const handleCancelConfirm = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      order_code: orderDetail?.code,
      button_name: 'confirm_cancel_button',
      button_location: 'order_cancel_dialog',
      user_id: user?.cunstomerId,
    });
    if (!selectedOrder) return;

    cancelOrderMutation.mutate({
      order_code: selectedOrder,
      cancel_code: 'user_no_driver_accept', // Always use this reason
      comment: cancelComment,
    });
    // Don't automatically navigate back - let the onSuccess handler in mutation handle it
  };

  const handleHomeClick = () => {
    if (!pathName.includes('order-list')) {
      onClose?.();
    }
    router.push(routes.home);
  };

  const handleCloseTrackingDrawer = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      order_code: orderDetail?.code,
      button_name: 'return_button',
      button_location: 'order_cancel_dialog',
      user_id: user?.cunstomerId,
    });
    setShowCancelDialog(false);
  };

  const handleTrackingSheetClose = useCallback((open: boolean) => {
    if (!open) {
      console.log('[OrderNotifications] Closing tracking sheet');
      setShowTrackingSheet(false);
      // Clear URL after animation completes
      setTimeout(() => {
        setTrackingUrl('');
      }, 300);
    }
  }, []);

  const handleReturnCancelClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      order_code: orderDetail?.code,
      button_name: 'return_cancel_button',
      button_location: 'order_cancel_dialog',
      user_id: user?.cunstomerId,
    });
  };

  const handleWaitingDialogClose = (open: boolean) => {
    if (!open) {
      // User closed the dialog (either by clicking outside or escape)
      handleReturnCancelClick();
    }
    setShowWaitingDialog(open);
  };

  // const handleClose = () => {
  //   if (isMobile) {
  //     router.push(routes.home);
  //   } else {
  //     onClose?.();
  //   }
  // };

  return (
    <div className="relative" data-testid="order-list-screen">
      <div
        className={`max-w-md md:w-full relative bg-white ${activeTab === 'detail' && orderDetail?.tracking_link ? 'pb-32' : 'pb-20'}`}
        // style={{ marginBottom: isNavigationVisible ? bottomPosition : 20 }}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Header */}
          <div className="p-4 flex items-center justify-between">
            {activeTab === 'detail' ? (
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBackToListing}
                  className="[&_svg]:size-5"
                  data-testid="order-detail-back"
                >
                  <ChevronLeft />
                </Button>
                <h1
                  className="text-lg font-medium text-center"
                  data-testid="order-detail-title"
                >
                  Chi tiết đơn hàng
                </h1>
              </div>
            ) : (
              <>
                {/* <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="[&_svg]:size-5 [&_svg]:text-neutral-60"
                >
                  <X />
                </Button> */}
                <h1
                  className="text-lg font-medium text-center"
                  data-testid="order-list-title"
                >
                  Danh sách đơn hàng
                </h1>
              </>
            )}
            <div className="flex items-center">
              {
                activeTab === 'detail' &&
                orderDetail &&
                shouldShowCancelButton(orderDetail) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelClick}
                    className={`text-sm pl-3 pr-0 underline ${
                      cancelButtonEnabled ? 'text-primary' : 'text-neutral-40'
                    }`}
                    data-testid="order-detail-cancel-order"
                  >
                    Hủy đơn
                  </Button>
                ) : null
                // <div className="w-10" />
              }
              <Button
                variant="ghost"
                size="sm"
                onClick={handleHomeClick}
                data-testid={
                  activeTab === 'detail'
                    ? 'order-detail-home'
                    : 'order-list-home'
                }
              >
                <House />
              </Button>
            </div>
          </div>

          <TabsContent value="listing" className="mt-0">
            <div className="p-4 space-y-3" data-testid="order-list-scroll">
              {!isAuthenticated ? (
                // No login state
                <div className="flex flex-col items-center justify-center py-12 text-neutral-40">
                  <Image
                    src="/static/images/store-empty.webp"
                    alt="Cần đăng nhập"
                    width={120}
                    height={120}
                    className="mb-4 opacity-80"
                  />
                  <div className="text-lg font-semibold mb-2">
                    Bạn cần đăng nhập để xem đơn hàng
                  </div>
                  <div className="text-sm text-center mb-4">
                    Đăng nhập để theo dõi đơn hàng
                  </div>
                  <Button
                    onClick={() => setShowLoginDialog(true)}
                    className="bg-primary-50 hover:bg-primary-60 text-white px-6 py-2"
                  >
                    Đăng nhập
                  </Button>
                </div>
              ) : historyLoading ? (
                // Skeleton placeholders for order history list
                <>
                  <Skeleton className="h-4 w-24 mb-2" />
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full mb-4 rounded-lg" />
                  ))}
                </>
              ) : orderHistory?.orders?.length === 0 ? (
                <div
                  className="flex flex-col items-center justify-center py-12 text-neutral-40"
                  data-testid="order-list-empty"
                >
                  <Image
                    src="/static/images/store-empty.webp"
                    alt="Không có đơn hàng nào"
                    width={120}
                    height={120}
                    className="mb-4 opacity-80"
                  />
                  <div className="text-lg font-semibold mb-2">
                    Bạn không có đơn nào
                  </div>
                  <div className="text-sm text-center">
                    Bạn chưa có đơn hàng nào gần đây.
                    <br />
                    Hãy đặt món để thưởng thức ngay!
                  </div>
                </div>
              ) : (
                <div data-testid="order-list">
                  {/* <div className="text-sm text-neutral-60 mb-4">Hôm nay</div> */}
                  {orderHistory?.orders?.map((order, index) => {
                    const statusInfo = getStatusInfo(order.status);
                    const isToday = dayjs.unix(order.create_at).isToday();
                    return (
                      <Card
                        key={order.code}
                        className="cursor-pointer hover drop-shadow-md transition-shadow"
                        onClick={() => handleOrderSelect(order.code)}
                        data-testid="order-list-card"
                        data-index={index}
                        data-order-code={order.code}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className="text-sm text-neutral-60"
                                  data-testid="order-list-card-code"
                                >
                                  Đơn: #{order.code}
                                </span>
                              </div>
                              <div
                                className="text-sm font-bold mb-1 text-secondary-40"
                                data-testid="order-list-card-store-name"
                              >
                                {order.store_name}
                              </div>
                              <div
                                className={`text-xs font-bold ${statusInfo.color}`}
                                data-testid="order-list-card-status"
                              >
                                {statusInfo.text}
                              </div>
                            </div>
                            <div className="text-right">
                              <div
                                className="text-sm text-neutral-60 mb-1"
                                data-testid="order-list-card-time"
                              >
                                {isToday
                                  ? `Hôm nay ${formatTime(order.create_at)}`
                                  : formatUnixTime(
                                      order.create_at,
                                      DateFormat.DATE_TIME_24
                                    )}
                              </div>
                              <div
                                className="font-bold"
                                data-testid="order-list-card-total"
                              >
                                {formatCurrency(order.total_payment)}
                              </div>
                            </div>
                          </div>
                          {/* Rating button for completed orders */}
                          {order.status === 'COMPLETED' && (
                            <div className="flex justify-end mt-3 pt-3 border-t border-neutral-10">
                              {order.rating_summary ? (
                                order.rating_summary.comment ? (
                                  <span className="flex items-center gap-2 text-sm text-green-60 font-medium">
                                    Đã đánh giá
                                  </span>
                                ) : (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={(e) =>
                                      handleCommentClick(e, order)
                                    }
                                    className="flex items-center gap-2 w-32 rounded-xl"
                                    data-testid="order-list-card-comment-button"
                                  >
                                    Nhận xét
                                  </Button>
                                )
                              ) : (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={(e) => handleRatingClick(e, order)}
                                  className="flex items-center gap-2 w-32 rounded-xl"
                                  data-testid="order-list-card-rating-button"
                                >
                                  Đánh giá
                                </Button>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="detail" className="mt-0">
            {detailLoading ? (
              // Skeleton placeholders for order detail
              <div className="p-4 space-y-4">
                <Skeleton className="h-8 w-1/2 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-2" />
                <Skeleton className="h-40 w-full rounded-lg mb-4" />
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-6 w-64 mb-2" />
                <Skeleton className="h-8 w-full rounded-lg" />
              </div>
            ) : orderDetail ? (
              <div className="space-y-4" data-testid="order-detail-screen">
                {/* Header with Status and Progress */}
                <div className="bg-white p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-3 rounded-full ${getStatusInfo(orderDetail.status).color}`}
                    >
                      {getStatusInfo(orderDetail.status).icon}
                    </div>
                    <div className="flex-1">
                      <h3
                        className="font-medium text-lg"
                        data-testid="order-detail-status-title"
                      >
                        {getStatusInfo(orderDetail.status).text}
                      </h3>
                      <p
                        className="text-sm text-neutral-60"
                        data-testid="order-detail-status-desc"
                      >
                        {getStatusInfo(orderDetail.status).description}
                      </p>
                    </div>
                  </div>
                  {/* Progress Stepper */}
                  {showStepper && (
                    <div
                      className="w-full px-4"
                      data-testid="order-detail-stepper"
                    >
                      <Stepper
                        value={getStepperValue(orderDetail.status)}
                        orientation="horizontal"
                        className="w-full"
                      >
                        <StepperItem
                          step={1}
                          className="flex-1"
                          completed={getStepStatus(1, orderDetail.status)}
                        >
                          <StepperTrigger className="flex flex-col items-center gap-2">
                            <StepperIndicator className="size-8 flex items-center justify-center">
                              <Image
                                src={`${assetPrefix}/static/images/step1.png`}
                                alt="Order Confirmed"
                                width={24}
                                height={24}
                                className="object-contain"
                              />
                            </StepperIndicator>
                          </StepperTrigger>
                          <StepperSeparator />
                        </StepperItem>
                        <StepperItem
                          step={2}
                          className="flex-1"
                          completed={getStepStatus(2, orderDetail.status)}
                          loading={orderDetail.status === 'ASSIGNING'}
                        >
                          <StepperTrigger className="flex flex-col items-center gap-2">
                            <StepperIndicator className="size-8 flex items-center justify-center">
                              <Image
                                src={`${assetPrefix}/static/images/step2.png`}
                                alt="Driver Assigned"
                                width={24}
                                height={24}
                                className="object-contain"
                              />
                            </StepperIndicator>
                          </StepperTrigger>
                          <StepperSeparator />
                        </StepperItem>
                        <StepperItem
                          step={3}
                          className="flex-1"
                          completed={getStepStatus(3, orderDetail.status)}
                          loading={orderDetail.status === 'BOARDED'}
                        >
                          <StepperTrigger className="flex flex-col items-center gap-2">
                            <StepperIndicator className="size-8 flex items-center justify-center">
                              <Image
                                src={`${assetPrefix}/static/images/step3.png`}
                                alt="On Delivery"
                                width={24}
                                height={24}
                                className="object-contain"
                              />
                            </StepperIndicator>
                          </StepperTrigger>
                          <StepperSeparator />
                        </StepperItem>
                        <StepperItem
                          step={4}
                          className="w-fit"
                          completed={getStepStatus(4, orderDetail.status)}
                        >
                          <StepperTrigger className="flex flex-col items-center gap-2">
                            <StepperIndicator className="size-8 flex items-center justify-center">
                              <Image
                                src={`${assetPrefix}/static/images/step4.png`}
                                alt="Completed"
                                width={24}
                                height={24}
                                className="object-contain"
                              />
                            </StepperIndicator>
                          </StepperTrigger>
                        </StepperItem>
                      </Stepper>
                    </div>
                  )}
                </div>
                <div
                  className="text-xs text-neutral-50 p-4"
                  data-testid="order-detail-code"
                >
                  {formatUnixTime(
                    orderDetail.order_time,
                    DateFormat.DATE_TIME_24
                  )}{' '}
                  - Mã đơn: {orderDetail.code}
                </div>

                {orderDetail.status === 'COMPLETED' &&
                  orderDetail.rating_summary && (
                    <Card className="mx-4">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-bold text-neutral-90">
                            Bạn đã đánh giá cho quán!
                          </h4>
                          {/* <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const orderDetailForModal = {
                              code: orderDetail.code,
                              store_name: orderDetail.store_name,
                              store_image_url:
                                orderDetail.store_image_url ||
                                '/static/images/empty-category.webp',
                            };
                            openCommentModal(
                              orderDetailForModal,
                              orderDetail.rating_summary!.overall
                            );
                          }}
                          className="text-primary-50 border-primary-20 hover:bg-primary-5"
                        >
                          Nhận xét
                        </Button> */}
                        </div>
                        <div className="flex items-center gap-1 border-t border-dashed pt-2 text-neutral-90 font-bold">
                          <span className="flex-none">
                            {orderDetail.rating_summary.overall}
                          </span>
                          <Star className="flex-none size-4 fill-primary-40 text-primary-40" />
                          {orderDetail.rating_summary.comment && (
                            <>
                              <span className="flex-none">•</span>
                              {/* comment được phép co và truncate */}
                              <span className="flex-1 min-w-0 text-neutral-50 font-normal truncate">
                                {orderDetail.rating_summary.comment}
                              </span>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Store Info */}
                <Card
                  onClick={() => {
                    router.push(ROUTES.storeDetail(orderDetail.slug));
                    onClose?.();
                  }}
                  className="mx-4 border border-neutral-10 drop-shadow-md"
                  data-testid="order-detail-store-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="size-10 bg-neutral-20 rounded-full flex items-center justify-center">
                        <span className="text-lg">🍽️</span>
                      </div>
                      <div>
                        <div
                          className="font-medium text-lg"
                          data-testid="order-detail-store-name"
                        >
                          {orderDetail.store_name}
                        </div>
                        <div
                          className="text-sm text-green-60"
                          data-testid="order-detail-store-code"
                        >
                          {orderDetail.delivery_order_id}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Delivery Address */}
                {/* <Card className="mx-4 border border-neutral-10 drop-shadow-md">
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1 w-full justify-between">
                    <User className="size-5 text-neutral-60" />
                    <h4 className="font-medium">{orderDetail.receiver.name}</h4>
                  </div>
                  <div className="flex items-center gap-2 mb-1 w-full justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="size-4 text-neutral-60" />
                      <span className="text-sm font-medium">Liên hệ</span>
                    </div>
                    <div className="text-sm">{orderDetail.receiver.mobile}</div>
                  </div>
                  <div className="flex items-start gap-2 mb-1 w-full justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="size-4 text-neutral-60" />
                      <span className="text-sm font-medium">Địa chỉ</span>
                    </div>
                    <div className="text-sm line-clamp-2 max-w-60 text-right">
                      {orderDetail.receiver.address}
                    </div>
                  </div>
                </CardContent>
              </Card> */}
                <Card
                  className="mx-4 border border-neutral-10 drop-shadow-md"
                  data-testid="order-detail-recipient-card"
                >
                  <CardHeader className="pb-3 p-4 border-b">
                    <h4 className="font-medium">Thông tin người nhận</h4>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div>
                      <div className="flex items-center gap-3 p-3 rounded-lg">
                        <div className="p-2 rounded-full">
                          <User className="size-5" />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium block">Tên</span>
                          <h4
                            className="text-sm"
                            data-testid="order-detail-recipient-name"
                          >
                            {orderDetail.receiver.name}
                          </h4>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 rounded-lg">
                        <div className="p-2 rounded-full">
                          <Phone className="size-4" />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium block">
                            Số điện thoại
                          </span>
                          <span
                            className="text-sm"
                            data-testid="order-detail-recipient-phone"
                          >
                            {orderDetail.receiver.mobile}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 rounded-lg">
                        <div className="p-2 rounded-full mt-1">
                          <MapPin className="size-4" />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium block mb-1">
                            Địa chỉ giao hàng
                          </span>
                          <p
                            className="text-sm leading-relaxed"
                            data-testid="order-detail-recipient-address"
                          >
                            {orderDetail.receiver.address}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Order Summary */}
                <Card
                  className="mx-4 border border-neutral-10 drop-shadow-md"
                  data-testid="order-detail-summary-card"
                >
                  <CardHeader className="pb-3 p-4 border-b">
                    <h4 className="font-medium">Tóm tắt đơn</h4>
                  </CardHeader>
                  <CardContent className="p-4">
                    {orderDetail.items.map((item, idx) => (
                      <div
                        key={item.id}
                        className="flex flex-col"
                        data-testid="order-detail-item"
                        data-index={idx}
                      >
                        <div className="flex justify-between text-sm">
                          <span data-testid="order-detail-item-name">
                            {item.quantity}x {item.name}
                          </span>
                          <span data-testid="order-detail-item-price">
                            {formatCurrency(item.price * item.quantity)}
                          </span>
                        </div>
                        {item.customizations && (
                          <div className="text-xs text-neutral-60 mt-1">
                            {item.customizations.map((customization) => (
                              <div
                                key={customization.id}
                                className="flex items-center justify-between gap-1 pl-2"
                              >
                                {customization.quantity}x{' '}
                                {customization.option_name}
                                <span>
                                  {formatCurrency(customization.price)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {item.note && (
                          <div className="text-xs text-neutral-60 mt-1 italic">
                            {item.note}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card
                  className="mx-4 border border-neutral-10 drop-shadow-md"
                  data-testid="order-detail-payment-card"
                >
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Tạm tính</span>
                        <span
                          className="font-semibold"
                          data-testid="order-detail-subtotal"
                        >
                          {formatCurrency(orderDetail.item_fee)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>
                          Phí ship{' '}
                          {orderDetail.distance > 0 &&
                            `(${orderDetail.distance} km)`}
                        </span>
                        <span
                          className="font-semibold"
                          data-testid="order-detail-shipping-fee"
                        >
                          {formatCurrency(orderDetail.delivery_fee)}
                        </span>
                      </div>
                      {orderDetail.applied_promo?.some(
                        (promo) =>
                          promo.name?.toLowerCase() === 'amori' &&
                          promo.scope?.toUpperCase() === 'PRIVATE'
                      ) ? (
                        <div className="flex items-center gap-2 text-sm text-blue-50 rounded-md">
                          <Image
                            src="/static/images/new-bot-avt.webp"
                            alt="Amorii Freeship"
                            width={24}
                            height={24}
                            className="size-6 object-contain shrink-0"
                          />
                          <span>Freeship từ Amori</span>
                          <span
                            className="ml-auto font-bold text-blue-50"
                            data-testid="order-detail-shipping-discount"
                          >
                            -{formatCurrency(orderDetail.delivery_discount)}
                          </span>
                        </div>
                      ) : orderDetail.delivery_discount > 0 ? (
                        <div className="flex justify-between text-sm text-red-60">
                          <span>Giảm giá vận chuyển</span>
                          <span
                            className="font-semibold text-primary-50"
                            data-testid="order-detail-shipping-discount"
                          >
                            -{formatCurrency(orderDetail.delivery_discount)}
                          </span>
                        </div>
                      ) : null}
                      {orderDetail.applied_promo &&
                        orderDetail.applied_promo.length > 0 &&
                        orderDetail.applied_promo.map(
                          (promo) =>
                            promo.name?.toLowerCase() === 'amori' &&
                            (promo.scope?.toUpperCase() === 'PRIVATE' ||
                              promo.scope?.toUpperCase() ===
                                'MERCHANT') ? null : (
                              <div
                                key={promo.promo_code}
                                className="flex justify-between text-sm text-red-60"
                                data-testid="order-detail-promo-discount"
                              >
                                <span>{promo.name}</span>
                                <span className="font-semibold text-primary-50">
                                  -{formatCurrency(promo.total_discount_value)}
                                </span>
                              </div>
                            )
                          // <div
                          //   key={promo.promo_code}
                          //   className="flex justify-between text-sm text-red-60"
                          // >
                          //   <span>{promo.name}</span>
                          //   <span className="font-semibold text-primary-50">
                          //     -{formatCurrency(promo.total_discount_value)}
                          //   </span>
                          // </div>
                        )}
                      {orderDetail.merchant_discount > 0 && (
                        <div
                          className="flex justify-between text-sm text-red-60"
                          data-testid="order-detail-merchant-discount"
                        >
                          <span>Giảm giá từ quán</span>
                          <span className="font-semibold text-primary-50">
                            -{formatCurrency(orderDetail.merchant_discount)}
                          </span>
                        </div>
                      )}
                      {orderDetail.platform_discount > 0 && (
                        <div className="flex justify-between text-sm text-red-60">
                          <span>Giảm giá platform</span>
                          <span className="font-semibold text-primary-50">
                            -{formatCurrency(orderDetail.platform_discount)}
                          </span>
                        </div>
                      )}

                      <div className="border-t pt-2">
                        <div className="flex justify-between font-medium">
                          <span>Tổng cộng</span>
                          <span
                            className="font-semibold"
                            data-testid="order-detail-total-payment"
                          >
                            {formatCurrency(orderDetail.total_payment)}
                          </span>
                        </div>
                        <div className="text-xs text-neutral-60 mt-1">
                          Thanh toán khi nhận hàng
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="p-4 text-center">
                <p>Không tìm thấy thông tin đơn hàng</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        <LoginDialog
          open={showLoginDialog}
          onOpenChange={setShowLoginDialog}
          location="amori-login"
        />

        {/* Tracking Sheet for WebInApp - Fullsize to prevent accidental outside clicks */}
        <Sheet open={showTrackingSheet} onOpenChange={handleTrackingSheetClose}>
          <SheetContent
            className="size-full p-0 max-w-none"
            side={isMobile ? 'bottom' : 'right'}
            style={{ zIndex: 10001 }}
          >
            <SheetHeader className="border-b py-3 px-4">
              <div className="flex items-center justify-between">
                <SheetTitle>Theo dõi hành trình đơn hàng</SheetTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleTrackingSheetClose(false)}
                  className="size-8 [&_svg]:size-5"
                >
                  <X />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 h-[calc(100%-60px)] p-0 overflow-hidden">
              {trackingUrl && (
                <iframe
                  src={trackingUrl}
                  className="size-full border-0"
                  title="Theo dõi hành trình đơn hàng"
                  loading="lazy"
                  sandbox="allow-same-origin allow-scripts allow-navigation allow-forms allow-pointer-lock allow-top-navigation"
                  style={{
                    touchAction: 'auto',
                    pointerEvents: 'auto',
                  }}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Cancel Order Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent
            className="max-w-sm md:max-w-md rounded-xl"
            data-testid="order-detail-cancel-dialog"
          >
            <DialogHeader>
              <DialogTitle>Bạn muốn hủy đơn hàng này?</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-neutral-60 text-sm text-center">
                Bạn sẽ không bị tính phí đối với đơn hàng này.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="secondary"
                onClick={handleCloseTrackingDrawer}
                className="flex-1 rounded-full"
                data-testid="order-detail-cancel-dialog-rollback"
              >
                Quay lại
              </Button>
              <Button
                onClick={handleCancelConfirm}
                disabled={cancelOrderMutation.isPending}
                className="flex-1 bg-primary text-white rounded-full"
                data-testid="order-detail-cancel-dialog-confirm"
              >
                {cancelOrderMutation.isPending ? 'Đang hủy...' : 'Hủy đơn hàng'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Waiting Dialog - Show when trying to cancel before 20 minutes */}
        <Dialog
          open={showWaitingDialog}
          onOpenChange={handleWaitingDialogClose}
        >
          <DialogContent
            className="max-w-sm md:max-w-md rounded-xl"
            data-testid="order-detail-waiting-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-center">
                Chúng tôi đang tìm tài xế gần nhất cho bạn.
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-neutral-60 text-sm text-center">
                Bạn có thể hủy đơn nếu chưa có tài xế sau 20 phút.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                onClick={() => {
                  handleReturnCancelClick();
                  setShowWaitingDialog(false);
                }}
                className="w-full bg-primary text-white rounded-full"
                data-testid="order-detail-waiting-dialog-rollback"
              >
                Quay lại
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fixed Tracking Button at Bottom - Only show on detail tab */}
        {activeTab === 'detail' && orderDetail?.tracking_link && (
          <div
            // className="w-full md:w-40"
            style={{
              position: isMobile ? 'fixed' : 'unset',
              left: 0,
              right: 0,
              background: '#fff',
              borderTop: '1px solid #F2F2F2',
              padding: 16,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
              zIndex: 50,
              bottom: isNavigationVisible
                ? 'calc(env(safe-area-inset-bottom, 0px) + 64px)'
                : 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <div style={{ maxWidth: 448, margin: '0 auto' }}>
              <Button
                onClick={() => handleTrackingClick(orderDetail.tracking_link)}
                className="w-full bg-primary-50 hover:bg-primary-60 text-white py-3 rounded-lg font-medium"
                data-testid="order-detail-track-button"
              >
                Theo dõi hành trình đơn hàng
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
